import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RiCalendarEventLine, RiUserLine } from "react-icons/ri";
import { FaDollarSign } from "react-icons/fa";
import { BiReceipt } from "react-icons/bi";
import { RefreshCw, Users, ChevronDown, ChevronUp } from "lucide-react";
import { ApiURL } from "../../setting/GlobleVariable";

const UserCreatedEvents = () => {
  const currentUser = useSelector((state) => state.user.user);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEvents, setExpandedEvents] = useState({});

  // Overall stats
  const [totalStats, setTotalStats] = useState({
    totalUsers: 0,
    totalEvents: 0,
    totalRevenue: 0,
    totalTicketsSold: 0,
  });

  useEffect(() => {
    fetchUserEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserEvents = async () => {
    setLoading(true);
    try {
      // Fetch all users with their events
      const response = await fetch(`${ApiURL}/user/admin/users-with-events`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('instapass_user_token'),
        },
      });

      if (response.ok) {
        const data = await response.json();
        const fetchedUsers = data.users || [];

        // Exclude the currently-logged-in admin and any users explicitly marked as admin
        const visibleUsers = fetchedUsers.filter(u => (
          u._id !== currentUser?._id && u.role !== 'admin' && u.userType !== 'admin'
        ));

        setUsers(visibleUsers);

        // Calculate overall stats from visible users only
        let totalUsers = visibleUsers.length;
        let totalEvents = 0;
        let totalRevenue = 0;
        let totalTicketsSold = 0;

        visibleUsers.forEach(user => {
          totalEvents += user.events?.length || 0;
          user.events?.forEach(event => {
            totalRevenue += event.totalRevenue || 0;
            totalTicketsSold += event.ticketsSold || 0;
          });
        });

        setTotalStats({
          totalUsers,
          totalEvents,
          totalRevenue,
          totalTicketsSold,
        });
      }
    } catch (error) {
      console.error('Error fetching user events:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleEventExpansion = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const statCards = [
    {
      id: "users",
      label: "Active Organizers",
      value: totalStats.totalUsers,
      icon: <RiUserLine className="w-5 h-5 text-blue-400" />,
      iconBg: "bg-blue-500/15",
      valueClass: "text-blue-400",
    },
    {
      id: "events",
      label: "Total Events",
      value: totalStats.totalEvents,
      icon: <RiCalendarEventLine className="w-5 h-5 text-violet-400" />,
      iconBg: "bg-violet-500/15",
      valueClass: "text-violet-400",
    },
    {
      id: "revenue",
      label: "Platform Revenue",
      value: `$${totalStats.totalRevenue.toLocaleString()}`,
      icon: <FaDollarSign className="w-4 h-4 text-emerald-400" />,
      iconBg: "bg-emerald-500/15",
      valueClass: "text-emerald-400",
    },
    {
      id: "tickets",
      label: "Tickets Sold",
      value: totalStats.totalTicketsSold,
      icon: <BiReceipt className="w-5 h-5 text-amber-400" />,
      iconBg: "bg-amber-500/15",
      valueClass: "text-amber-400",
    },
  ];

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="flex justify-center items-center h-64">
          <div className="w-10 h-10 rounded-full border-2 border-[#E52324]/30 border-t-[#E52324] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8" style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E52324]/15 border border-[#E52324]/20 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-[#E52324]" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-[22px] text-white" style={{ fontWeight: 800 }}>
              User Created Events
            </h1>
            <p className="text-white/30 text-[12px] mt-1">
              Monitor organizer-created events and platform performance.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={fetchUserEvents}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/[0.08] text-white/50 hover:text-white hover:bg-white/[0.03] text-[12px] transition-all w-fit"
          style={{ fontWeight: 600 }}
        >
          <RefreshCw className="w-4 h-4" />
          Reload
        </button>
      </div>

      {/* Stats Cards */}
      <div className="mb-8">
        <h3 className="text-white/85 text-[16px] mb-4" style={{ fontWeight: 700 }}>
          Platform Statistics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((card) => (
            <div
              key={card.id}
              className="bg-[#0B1120] border border-[#1a1f30] rounded-2xl p-4 min-h-[116px] flex flex-col justify-between"
            >
              <div className={`w-9 h-9 rounded-lg ${card.iconBg} flex items-center justify-center`}>
                {card.icon}
              </div>
              <div>
                <p className={`text-[22px] ${card.valueClass}`} style={{ fontWeight: 800 }}>
                  {card.value}
                </p>
                <p className="text-[12px] text-white/35">{card.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Users and Their Events */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-[17px] text-white" style={{ fontWeight: 700 }}>
            Event Organizers ({users.length})
          </h3>
        </div>

        {users.length > 0 ? (
          <div className="space-y-4 max-h-[650px] overflow-y-auto pr-1">
            {users.map((user) => {
              const userTotalRevenue = user.events?.reduce((sum, event) => sum + (event.totalRevenue || 0), 0) || 0;
              const userTotalTickets = user.events?.reduce((sum, event) => sum + (event.ticketsSold || 0), 0) || 0;
              
              return (
                <div key={user._id} className="w-full bg-[#0B1120] border border-[#1a1f30] rounded-2xl p-4">
                  {/* User Header */}
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4 border-b border-[#1a1f30] pb-3">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-[#E52324] to-[#ff6b6b] rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {(user.firstName?.[0] || 'U').toUpperCase()}{(user.lastName?.[0] || '').toUpperCase()}
                      </div>
                      <div>
                        <div className="text-[16px] text-white" style={{ fontWeight: 700 }}>
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-[13px] text-white/60">{user.email}</div>
                        <div className="text-[11px] text-white/35">
                          Joined: {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { dateStyle: 'medium' }) : 'Unknown'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right min-w-[200px]">
                      <div className="text-[15px] text-emerald-400" style={{ fontWeight: 700 }}>
                        ${userTotalRevenue.toLocaleString()}
                      </div>
                      <div className="text-[11px] text-white/45 uppercase tracking-widest">Total Revenue</div>
                      <div className="text-[11px] text-white/35">
                        {user.events?.length || 0} events • {userTotalTickets} tickets sold
                      </div>
                    </div>
                  </div>

                  {/* User's Events */}
                  {user.events && user.events.length > 0 ? (
                    <div className="space-y-3">
                      <div className="text-[12px] uppercase tracking-widest text-[#E52324]/80 mb-2" style={{ fontWeight: 700 }}>
                        Events ({user.events.length}):
                      </div>
                      {user.events.map((event) => (
                        <div key={event._id} className="bg-[#0F172A] border border-[#1a2744] rounded-xl p-4">
                          {/* Event Header */}
                          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                            <div className="flex items-center space-x-3">
                              <img
                                className="h-10 w-10 rounded-lg object-cover border border-[#1a2744]"
                                src={event.image ? `${ApiURL.replace('/api', '')}/uploads/${event.image}` : '/default-event.jpg'}
                                alt={event.name || event.eventName}
                                onError={(e) => { e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjOWNhM2FmIi8+Cjx0ZXh0IHg9IjIwIiB5PSIyNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iI2ZmZmZmZiIgZm9udC1zaXplPSIxNiIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIj5FdmVudDwvdGV4dD4KPHN2Zz4='; }}
                              />
                              <div>
                                <div className="text-[14px] text-white" style={{ fontWeight: 700 }}>
                                  {event.name || event.eventName || 'Unnamed Event'}
                                </div>
                                <div className="text-[12px] text-white/50">
                                  {event.venue?.name || event.venue?.location || event.location || 'No Location'}
                                </div>
                                <div className="text-[11px] text-white/30">
                                  {event.occurs_at ? new Date(event.occurs_at).toLocaleDateString('en-US', { dateStyle: 'medium' }) : 'No Date Set'}
                                </div>
                              </div>
                            </div>
                            <div className="text-right min-w-[120px]">
                              <div className="text-[14px] text-emerald-400" style={{ fontWeight: 700 }}>
                                ${(event.totalRevenue || 0).toLocaleString()}
                              </div>
                              <div className="text-[10px] uppercase tracking-widest text-white/35">Revenue</div>
                              <div className="text-[11px] text-white/35">
                                {event.ticketsSold || 0} / {event.totalTickets || 0} sold
                              </div>
                              {event.userTickets && event.userTickets.length > 0 && (
                                <button
                                  onClick={() => toggleEventExpansion(event._id)}
                                  className="mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[#E52324]/25 text-[#E52324] text-[10px] hover:bg-[#E52324]/10 transition-colors"
                                >
                                  {expandedEvents[event._id] ? (
                                    <ChevronUp className="w-3 h-3" />
                                  ) : (
                                    <ChevronDown className="w-3 h-3" />
                                  )}
                                  {expandedEvents[event._id] ? 'Hide Details' : 'Show Details'}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Event Ticket Details */}
                          {expandedEvents[event._id] && event.userTickets && event.userTickets.length > 0 && (
                            <div className="border-t border-[#1a2744] pt-3 mt-2">
                              <div className="text-[11px] text-white/55 mb-2" style={{ fontWeight: 600 }}>
                                Ticket Types:
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {event.userTickets.map((ticket, idx) => {
                                  const sold = ticket.sold || 0;
                                  const remaining = ticket.remaining !== undefined ? ticket.remaining : 0;
                                  const quantity = parseInt(ticket.quantity) || (sold + remaining);
                                  const percentSold = quantity > 0 ? Math.round((sold / quantity) * 100) : 0;
                                  const ticketRevenue = (sold * parseFloat(ticket.price)) || 0;
                                  
                                  return (
                                    <div key={ticket.id || idx} className="bg-[#111b30] rounded-lg px-3 py-2 border border-[#22345b]">
                                      <div className="flex justify-between items-center mb-1">
                                        <span className="text-[11px] text-white/85" style={{ fontWeight: 600 }}>
                                          {ticket.ticketName}
                                        </span>
                                        <span className="text-[10px] text-emerald-400">${ticket.price}</span>
                                      </div>
                                      <div className="text-[10px] text-white/55 mb-1">
                                        {sold} / {quantity} sold ({percentSold}%)
                                      </div>
                                      <div className="w-full bg-[#1a2744] rounded-full h-1 mb-1">
                                        <div 
                                          className="bg-[#E52324] h-1 rounded-full transition-all" 
                                          style={{ width: `${percentSold}%` }}
                                        ></div>
                                      </div>
                                      <div className="text-[9px] text-white/45 flex justify-between">
                                        <span>{remaining} remaining</span>
                                        <span className="text-emerald-400">${ticketRevenue.toFixed(2)}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-white/35">
                      <RiCalendarEventLine className="mx-auto h-8 w-8 mb-2" />
                      <div className="text-[13px]">No events created yet</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="w-full bg-[#0B1120] border border-[#1a1f30] rounded-2xl py-10 px-4 text-center">
            <RiUserLine className="mx-auto h-12 w-12 text-white/25 mb-4" />
            <h3 className="text-[16px] text-white mb-2" style={{ fontWeight: 700 }}>
              No event organizers yet
            </h3>
            <p className="text-[13px] text-white/40">
              Users haven't created any events on the platform.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserCreatedEvents;